<template>
    <dialog-window v-model="isOpen" data-id="editEmailDialog" :title="title" @close="closeDialogHandler">
        <v-form ref="form" @submit.prevent="handleSubmit">
            <form-section :is-loading="isLoading">
                <input-field
                    id="email"
                    v-model="formData.email"
                    data-id="editEmailDialogEmailField"
                    :disabled="isSubmitting"
                    :pattern="FIELD_PATTERNS.EMAIL"
                    :pattern-error="t('validation.emailRequired')"
                    :required="true"
                    :label="t('profile.email')"
                />

                <input-field
                    id="emailConfirm"
                    v-model="formData.emailConfirm"
                    data-id="editEmailDialogConfirmEmailField"
                    :reference-value="formData.email"
                    :disabled="isSubmitting"
                    :pattern="FIELD_PATTERNS.EMAIL"
                    :reference-error-label="t('profile.emailsMustMatch')"
                    :pattern-error="t('validation.emailRequired')"
                    :required="true"
                    :label="t('profile.emailConfirm')"
                />

                <v-card-actions class="d-flex justify-end ma-0 pa-0">
                    <card-button
                        data-id="editEmailDialogCancelButton"
                        class="flex-1-0-0"
                        size="large"
                        :inactive-label="t('form.cancel')"
                        :disabled="isSubmitting"
                        @click="closeDialogHandler"
                    >
                        {{ t('profile.cancel') }}
                    </card-button>
                    <card-button
                        data-id="editEmailDialogSubmitButton"
                        type="submit"
                        class="flex-1-0-0"
                        size="large"
                        variant="flat"
                        :loading="isLoading"
                        :inactive-label="t('inquiry.sendMessage')"
                        :disabled="isSubmitting"
                    >
                        {{ t('profile.profileSave') }}
                    </card-button>
                </v-card-actions>
            </form-section>
        </v-form>
    </dialog-window>
    <notification-bar
        v-model:show="notificationBarState.show"
        :color="notificationBarState.color"
        :icon="notificationBarState.icon"
        :border="notificationBarState.border"
        :message="notificationBarState.message"
    />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@geta/kolumbus-frontend/features/authentication';
import { useContainer } from '@/plugins/inversify';
import { IProfileApi, IProfileApiId } from '@/features/Profile/services/Interface/IProfileApi';
import { FormSection, InputField, NotificationBar } from '@/features/common/components/form';
import { FIELD_PATTERNS } from '@/features/common/components/form/constants';
import { useFormData, useFormHandling, useNotification } from '@/features/common/components/form/composables';
import CardButton from '@/features/common/components/CardButton.vue';
import { IEditProfileDialog } from '../types';
import { useGlobalDialogStore } from '@/store/dialogStore';
import DialogWindow from '@/features/common/components/DialogWindow.vue';

const { t } = useI18n();
const userStore = useUserStore();
const container = useContainer();
const { isDialogOpen, closeDialog } = useGlobalDialogStore();
const profileApi = container.get<IProfileApi>(IProfileApiId);

const props = defineProps<IEditProfileDialog>();

const isOpen = computed(() => isDialogOpen(props.type));

const isLoading = ref(false);
interface UpdateEmailData {
    email: string;
    emailConfirm: string;
}

const updateEmailData: UpdateEmailData = {
    email: '',
    emailConfirm: ''
};

const { form, isSubmitting, validateForm, toggleIsSubmitting, isFormEmpty } = useFormHandling();
const { notificationBarState, notificationError, notificationSuccess } = useNotification();
const { formData, resetFormData } = useFormData(updateEmailData);

const saveData = async (data: UpdateEmailData) => {
    try {
        await profileApi.updateEmail({ email: data.email });
        await userStore.loadProfile();
        notificationBarState.value = notificationSuccess(t('profile.emailUpdatedSuccess'));
        return true;
    } catch {
        notificationBarState.value = notificationError(t('profile.emailUpdatedFailed'));
        return false;
    }
};

const submitForm = async () => {
    try {
        toggleIsSubmitting(true);
        const result = await saveData(formData.value);
        result && closeDialog(props.type);
    } finally {
        toggleIsSubmitting(false);
    }
};

const handleSubmit = async () => {
    if (isFormEmpty()) return;
    const { valid } = await validateForm();
    if (!valid) return;
    await submitForm();
};

const closeDialogHandler = () => {
    resetFormData(form);
    closeDialog(props.type);
};
</script>
